import React, { useMemo } from "react";
import { Autocomplete, Box, InputAdornment, TextField, createFilterOptions } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { COUNTRY_MAP, normalizeCountryCode } from "../../utils/Countries";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";

const COUNTRY_OPTIONS = Object.entries(COUNTRY_MAP)
  .map(([code, name]) => ({ code, name }))
  .sort((a, b) => a.name.localeCompare(b.name));
const filterCountries = createFilterOptions({ stringify: (option) => `${option.name} ${option.code}` });

function CountryDropdown({ value, onChange, label = "Country", sx = {},
  disabled = false, required = false, name, onBlur, ...props }) {
  const normalizedCode = normalizeCountryCode(value);
  const selected = useMemo(() => value
    ? COUNTRY_OPTIONS.find((country) => country.code === normalizedCode) || { code: value, name: value }
    : null, [normalizedCode, value]);
  const options = useMemo(() => selected && !COUNTRY_MAP[selected.code]
    ? [selected, ...COUNTRY_OPTIONS] : COUNTRY_OPTIONS, [selected]);

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selected}
      disabled={disabled}
      autoHighlight
      openOnFocus
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, current) => option.code === current.code}
      filterOptions={(items, state) => {
        const matches = filterCountries(items, state);
        const exact = items.find((item) => item.code === normalizeCountryCode(state.inputValue));
        return exact ? [exact, ...matches.filter((item) => item.code !== exact.code)] : matches;
      }}
      onChange={(_event, country) => onChange?.({ target: { name, value: country?.name || "" } })}
      onBlur={onBlur}
      clearText="Clear country"
      openText="Browse countries"
      closeText="Close countries"
      noOptionsText="No matching countries. Try a name or country code."
      sx={sx}
      slotProps={{
        paper: {
          sx: {
            mt: 0.75, p: 0.5, borderRadius: 2.5,
            border: "1px solid rgba(0,51,108,0.12)",
            boxShadow: "0 12px 32px rgba(0,51,108,0.14)", color: BRAND_BLUE,
            "& .MuiAutocomplete-listbox": { maxHeight: 300, p: 0.5 },
            "& .MuiAutocomplete-option": {
              borderRadius: 1.5, minHeight: 42, px: 1.5, my: 0.25,
              "&.Mui-focused": { bgcolor: "rgba(0,51,108,0.06)" },
              '&[aria-selected="true"]': { bgcolor: "rgba(100,131,21,0.10)", fontWeight: 600 },
              '&[aria-selected="true"].Mui-focused': { bgcolor: "rgba(100,131,21,0.16)" },
            },
          },
        },
      }}
      renderOption={(optionProps, option, { selected: isSelected }) => (
        <li {...optionProps}>
          <Box component="span" sx={{ flex: 1 }}>{option.name}</Box>
          {COUNTRY_MAP[option.code] && <Box component="span" sx={{ ml: 1, fontSize: 12, color: "text.secondary" }}>{option.code}</Box>}
          {isSelected && <CheckRoundedIcon sx={{ ml: 1, fontSize: 19, color: BRAND_GREEN }} />}
        </li>
      )}
      renderInput={(params) => (
        <TextField {...props} {...params} required={required} name={name} label={label} placeholder="Search countries…"
          sx={{
            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused, & .MuiInputBase-input": { color: BRAND_BLUE },
            "& .MuiOutlinedInput-root": {
              borderRadius: 2, bgcolor: "background.paper", transition: "box-shadow 150ms ease",
              "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,51,108,0.08)" },
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} /></InputAdornment>,
          }}
        />
      )}
    />
  );
}

export default CountryDropdown;
